<template>
    <svg v-if="kind === 'sixty'" :class="['inline-block shrink-0', sizeClass]" viewBox="4 1 30 38" fill="none" aria-hidden="true">
        <path :d="TRIANGLE_LOWER" :fill="COLORS.sixty.dark" />
        <path :d="TRIANGLE_UPPER" :fill="COLORS.sixty.light" />
    </svg>
    <svg v-else-if="kind === 'team'" :class="['inline-block shrink-0', sizeClass]" viewBox="4 1 30 38" fill="none" aria-hidden="true">
        <g transform="rotate(180 19 19.93)">
            <path :d="TRIANGLE_LOWER" :fill="COLORS.team.dark" />
            <path :d="TRIANGLE_UPPER" :fill="COLORS.team.light" />
        </g>
    </svg>
    <svg v-else-if="kind === 'self'" :class="['inline-block shrink-0', sizeClass]" viewBox="0 0 29.806 38" fill="none" aria-hidden="true">
        <g transform="translate(29.806 0) rotate(90)">
            <path :d="HALF_CIRCLE" :fill="COLORS.self.light" />
            <path :d="ACCENT_CIRCLE" :fill="COLORS.self.dark" />
        </g>
    </svg>
    <svg v-else-if="kind === 'upcoming'" :class="['inline-block shrink-0', sizeClass]" viewBox="0 0 30 38" fill="none" aria-hidden="true">
        <path
            d="M30 35.9947C29.915 31.3121 28.375 27.4616 25.38 24.4376C22.475 21.5097 19.02 20.0271 15.015 20.0004C6.715 19.9364 0.005 27.1789 0 36H30V35.9947Z"
            :fill="COLORS.upcoming.dark" />
        <path d="M0 3C0.085 7.97684 1.625 12.0694 4.62 15.2834C7.525 18.3953 10.98 19.9711 14.985 19.9995C23.285 20.0732 29.995 12.3755 30 3H0Z" :fill="COLORS.upcoming.light" />
        <rect y="35" width="30" height="3" :fill="COLORS.upcoming.dark" />
        <rect width="30" height="3" :fill="COLORS.upcoming.light" />
    </svg>
    <svg v-else-if="kind === 'unnamed'" :class="['inline-block shrink-0', sizeClass]" viewBox="4 1 30 38" fill="none" aria-hidden="true">
        <path :d="TRIANGLE_LOWER" :fill="COLORS.sixty.dark" />
        <path d="M4 1a15 15 0 0 0 30 0Z" :fill="COLORS.sixty.light" />
    </svg>
</template>

<script lang="ts">
export type ReflectionIconKind = "sixty" | "self" | "team" | "upcoming" | "unnamed";
export type ReflectionIconSize = "sm" | "md" | "title" | "hero";

interface Props {
    kind: ReflectionIconKind;
    size?: ReflectionIconSize;
}

// Rounded-corner triangle paths lifted from the design prototype
// (assessments-lilac.vercel.app/.card__mark). Each triangle is ~30 wide × 21
// tall (aspect ~1.43 — the "squat" look) and the two overlap ~5 units of
// vertical space so they read as a single stacked mark, not two disjoint
// shapes. Team reuses the same paths under a 180° rotation, so the two icons
// stay pixel-mirrored.
const TRIANGLE_LOWER =
    "M18.0068 17.3582C18.4983 16.686 19.5017 16.686 19.9933 17.3582L34.0234 36.5431C34.6179 37.3561 34.0373 38.5 33.0301 38.5L4.96987 38.5C3.9627 38.5 3.38208 37.3561 3.97662 36.5431L18.0068 17.3582Z";
const TRIANGLE_UPPER =
    "M18.0068 1.35819C18.4983 0.685996 19.5017 0.685995 19.9933 1.35818L34.0234 20.5431C34.6179 21.3561 34.0373 22.5 33.0301 22.5L4.96987 22.5C3.9627 22.5 3.38208 21.3561 3.97662 20.5431L18.0068 1.35819Z";
const HALF_CIRCLE =
    "M0 10.808C0.107667 16.3657 2.05833 20.9417 5.85517 24.5329C9.53483 28.0132 13.9112 29.7676 18.981 29.8056C29.4975 29.8816 37.9937 21.2806 38 10.8049L0 10.8049L0 10.808Z";
const ACCENT_CIRCLE =
    "M18.9999 11.1767C22.0862 11.1767 24.5881 8.67472 24.5881 5.58835C24.5881 2.50199 22.0862 0 18.9999 0C15.9137 0 13.4118 2.50199 13.4118 5.58835C13.4118 8.67472 15.9137 11.1767 18.9999 11.1767Z";

const COLORS = {
    sixty: { dark: "#E84C8D", light: "#F6A6C5" },
    team: { dark: "#5FA37C", light: "#BEE59F" },
    self: { light: "#FFD3A8", dark: "#FBB877" },
    upcoming: { dark: "#30A29D", light: "#81DADA" },
};

// Prototype boxes: 24×30 on card rows (`md`), square 26×26 on page titles
// (`title`, where the glyph letterboxes), 40px tall on empty-state cards.
const TALL_SIZES: Record<ReflectionIconSize, string> = {
    sm: "w-4 h-5",
    md: "w-6 h-7.5",
    title: "w-6.5 h-6.5",
    hero: "w-auto h-10",
};

const HOURGLASS_SIZES: Record<ReflectionIconSize, string> = {
    sm: "w-5 h-6",
    md: "w-6 h-7.5",
    title: "w-6.5 h-6.5",
    hero: "w-auto h-10",
};
</script>

<script setup lang="ts">
import { computed } from "vue";

const { kind, size = "md" } = defineProps<Props>();

const sizeClass = computed(() => {
    return kind === "upcoming" ? HOURGLASS_SIZES[size] : TALL_SIZES[size];
});
</script>
